// Bean-size measurement — pure JavaScript / Canvas (no OpenCV / WASM).
// Deterministic geometry, NOT an LLM estimate (doctrine §1.4: never invent numbers).
//
// Pipeline: decode → downscale → grayscale → Otsu → find the bright SHEET (largest bright
// connected component) → within it, threshold dark blobs (coin + beans + ink) → connected-
// components with accumulated image moments → equivalent-ellipse per blob → the big round
// blob is the COIN (⌀26 mm → scale) → the rest, filtered by size/shape, are beans.
//
// No engine to download, so it starts instantly and works in any browser.

export const METHOD_VERSION = "js-v1.0";

export interface PerBeanResult {
  len: number;    // mm (major axis)
  width: number;  // mm (minor axis)
  aspect: number; // len / width
  screen: number; // width in 1/64 inch
}

export interface MeasureResult {
  ok: boolean;
  error?: string;
  warnings: string[];
  coinMm: number;
  scaleMmPx: number | null;         // mm per pixel from the coin
  selftestCircleMm: number | null;  // (reserved) measured Ø of the printed 50 mm circle
  beans: PerBeanResult[];
  aggregate: {
    n: number;
    medianLen: number | null;
    medianWidth: number | null;
    medianAspect: number | null;
    medianScreen: number | null;
    spreadWidth: number | null; // IQR of width
  };
  annotated: string | null;         // data-URL of the annotated image for visual review
}

export const TUNABLES = {
  refDiscMm: 40.0,            // diameter of the solid green SCALE disc printed on the sheet
  boxLenMm: 261.0,            // long side of the green bean-zone box (second scale = disc-vs-box check)
  maxImagePx: 1500,           // downscale longest side to this before processing
  beanMaxLenMm: 16,           // upper safety only — NO lower size gate (small beans are valid!)
  beanMinAspect: 1.0,
  beanMaxAspect: 4.5,
  beanMinExtent: 0.45,        // shape sanity: reject hollow rings / thin fragments, not small beans
  minSheetAreaFrac: 0.10,     // the bright sheet must fill ≥ this of the frame
};

// ── Image decode → canvas (respects EXIF orientation; browser handles JPEG/HEIC/PNG) ──
async function fileToCanvas(file: File, maxPx: number): Promise<HTMLCanvasElement> {
  let bmp: ImageBitmap;
  try {
    bmp = await createImageBitmap(file, { imageOrientation: "from-image" } as ImageBitmapOptions);
  } catch {
    const url = URL.createObjectURL(file);
    try {
      const img = await new Promise<HTMLImageElement>((res, rej) => {
        const i = new Image();
        i.onload = () => res(i);
        i.onerror = () => rej(new Error("could not decode image"));
        i.src = url;
      });
      bmp = await createImageBitmap(img);
    } finally { URL.revokeObjectURL(url); }
  }
  const scale = Math.min(1, maxPx / Math.max(bmp.width, bmp.height));
  const w = Math.round(bmp.width * scale), h = Math.round(bmp.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(bmp, 0, 0, w, h);
  bmp.close?.();
  return canvas;
}

const median = (xs: number[]): number | null => {
  if (!xs.length) return null;
  const s = [...xs].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};
const quantile = (xs: number[], q: number): number => {
  const s = [...xs].sort((a, b) => a - b);
  const pos = (s.length - 1) * q, base = Math.floor(pos), rest = pos - base;
  return s[base + 1] !== undefined ? s[base] + rest * (s[base + 1] - s[base]) : s[base];
};

// Otsu threshold from a 256-bin histogram → the gray level that best splits dark/bright.
function otsu(hist: Float64Array, total: number): number {
  let sum = 0;
  for (let i = 0; i < 256; i++) sum += i * hist[i];
  let sumB = 0, wB = 0, maxVar = -1, thresh = 127;
  for (let t = 0; t < 256; t++) {
    wB += hist[t];
    if (wB === 0) continue;
    const wF = total - wB;
    if (wF === 0) break;
    sumB += t * hist[t];
    const mB = sumB / wB, mF = (sum - sumB) / wF;
    const between = wB * wF * (mB - mF) * (mB - mF);
    if (between > maxVar) { maxVar = between; thresh = t; }
  }
  return thresh;
}

interface Blob {
  count: number; cx: number; cy: number;
  major: number; minor: number; angle: number;
  minx: number; maxx: number; miny: number; maxy: number;
  extent: number; aspect: number;
}

// Connected-components (4-neighbour flood fill) over a boolean mask, accumulating the image
// moments needed for the equivalent ellipse. Only components ≥ minPx are returned.
function components(mask: Uint8Array, w: number, h: number, minPx: number): Blob[] {
  const labels = new Int32Array(w * h);
  const stack: number[] = [];
  const out: Blob[] = [];
  let label = 0;
  for (let start = 0; start < w * h; start++) {
    if (!mask[start] || labels[start]) continue;
    label++;
    let count = 0, sx = 0, sy = 0, sxx = 0, syy = 0, sxy = 0;
    let minx = w, maxx = 0, miny = h, maxy = 0;
    stack.push(start); labels[start] = label;
    while (stack.length) {
      const p = stack.pop() as number;
      const x = p % w, y = (p / w) | 0;
      count++; sx += x; sy += y; sxx += x * x; syy += y * y; sxy += x * y;
      if (x < minx) minx = x; if (x > maxx) maxx = x;
      if (y < miny) miny = y; if (y > maxy) maxy = y;
      if (x > 0)     { const q = p - 1; if (mask[q] && !labels[q]) { labels[q] = label; stack.push(q); } }
      if (x < w - 1) { const q = p + 1; if (mask[q] && !labels[q]) { labels[q] = label; stack.push(q); } }
      if (y > 0)     { const q = p - w; if (mask[q] && !labels[q]) { labels[q] = label; stack.push(q); } }
      if (y < h - 1) { const q = p + w; if (mask[q] && !labels[q]) { labels[q] = label; stack.push(q); } }
    }
    if (count < minPx) continue;
    const mx = sx / count, my = sy / count;
    const u20 = sxx / count - mx * mx;
    const u02 = syy / count - my * my;
    const u11 = sxy / count - mx * my;
    const common = Math.sqrt(Math.max((u20 - u02) * (u20 - u02) + 4 * u11 * u11, 0));
    const l1 = (u20 + u02 + common) / 2, l2 = (u20 + u02 - common) / 2;
    const major = 4 * Math.sqrt(Math.max(l1, 0));
    const minor = 4 * Math.sqrt(Math.max(l2, 0));
    const bboxArea = (maxx - minx + 1) * (maxy - miny + 1);
    out.push({
      count, cx: mx, cy: my, major, minor,
      angle: 0.5 * Math.atan2(2 * u11, u20 - u02),
      minx, maxx, miny, maxy,
      extent: count / Math.max(bboxArea, 1),
      aspect: minor > 0.5 ? major / minor : 99,
    });
  }
  return out;
}

export async function measureBeans(file: File, onProgress?: (s: string) => void): Promise<MeasureResult> {
  const warnings: string[] = [];
  const step = (s: string) => { try { onProgress?.(s); } catch { /* best-effort */ } };
  const result: MeasureResult = {
    ok: false, warnings, coinMm: 0, scaleMmPx: null, selftestCircleMm: null,
    beans: [], aggregate: { n: 0, medianLen: null, medianWidth: null, medianAspect: null, medianScreen: null, spreadWidth: null },
    annotated: null,
  };

  step("reading photo");
  const canvas = await fileToCanvas(file, TUNABLES.maxImagePx);
  const w = canvas.width, h = canvas.height, N = w * h;
  const ctx = canvas.getContext("2d")!;
  const rgba = ctx.getImageData(0, 0, w, h).data;

  // grayscale + global histogram
  const gray = new Uint8Array(N);
  const hist = new Float64Array(256);
  for (let i = 0, j = 0; i < N; i++, j += 4) {
    const g = (rgba[j] * 0.299 + rgba[j + 1] * 0.587 + rgba[j + 2] * 0.114) | 0;
    gray[i] = g; hist[g]++;
  }

  step("finding the sheet");
  const tGlobal = otsu(hist, N);
  const bright = new Uint8Array(N);
  for (let i = 0; i < N; i++) bright[i] = gray[i] > tGlobal ? 1 : 0;
  const brightComps = components(bright, w, h, Math.round(TUNABLES.minSheetAreaFrac * N * 0.3));
  let sheet: Blob | null = null;
  for (const c of brightComps) if (!sheet || c.count > sheet.count) sheet = c;

  // Region of interest = the sheet's bounding box (shrunk a touch), or the whole frame.
  let rx0 = 0, ry0 = 0, rx1 = w - 1, ry1 = h - 1;
  if (sheet && sheet.count > TUNABLES.minSheetAreaFrac * N) {
    const mx = Math.round((sheet.maxx - sheet.minx) * 0.02);
    const my = Math.round((sheet.maxy - sheet.miny) * 0.02);
    rx0 = sheet.minx + mx; rx1 = sheet.maxx - mx; ry0 = sheet.miny + my; ry1 = sheet.maxy - my;
  } else {
    warnings.push("Sheet not found — using the whole photo (shoot the white sheet on a darker surface).");
  }

  // Colored print (box, labels, scale disc are all green/blue). Coffee beans are NEVER green or
  // blue, so every colored pixel is dropped from the bean search. Two green features matter:
  //   • the largest colored region  = the bean-zone BOX (where to look for beans)
  //   • a solid green DISC of known ⌀ = the SCALE reference — color-detected (immune to lighting/
  //     shadow/glare) and rotation-invariant, unlike the metallic coin we abandoned.
  const colored = new Uint8Array(N);
  let coloredCount = 0;
  for (let i = 0, j = 0; i < N; i++, j += 4) {
    const r = rgba[j], g = rgba[j + 1], b = rgba[j + 2];
    const isGreen = g > 55 && g > r + 20 && g > b + 20;   // lenient so shadowed green still counts
    const isBlue = b > 55 && b > r + 20 && b > g + 20;
    if (isGreen || isBlue) { colored[i] = 1; coloredCount++; }
  }
  const colComps = coloredCount > 0.001 * N ? components(colored, w, h, Math.round(0.0003 * N)) : [];
  colComps.sort((a, b) => b.count - a.count);

  step("finding the box + scale disc");
  const minSide = 0.03 * Math.min(w, h);
  const bboxAspect = (c: Blob) => { const bw = c.maxx - c.minx + 1, bh = c.maxy - c.miny + 1; return Math.max(bw, bh) / Math.min(bw, bh); };

  // Bean-zone box = largest ELONGATED colored outline (bbox aspect > 1.8). colComps is sorted
  // largest-first, so find() returns the biggest match. Classify by shape, not raw size.
  let beanROI: { x0: number; y0: number; x1: number; y1: number } | null = null;
  const box = colComps.find((c) => bboxAspect(c) > 1.8 && Math.min(c.maxx - c.minx, c.maxy - c.miny) >= minSide) || null;
  if (box) {
    const mx = Math.round((box.maxx - box.minx) * 0.04), my = Math.round((box.maxy - box.miny) * 0.04);
    beanROI = { x0: box.minx + mx, y0: box.miny + my, x1: box.maxx - mx, y1: box.maxy - my };
  }
  if (!beanROI) warnings.push("Bean-zone box not found — reprint the green sheet, or stray marks may be measured.");

  // Scale disc = largest ROUND, well-filled colored blob (the solid green dot).
  const refDisc = colComps.find((c) => bboxAspect(c) <= 1.4 && c.extent >= 0.6 && Math.min(c.maxx - c.minx, c.maxy - c.miny) >= minSide) || null;
  if (!refDisc) {
    result.error = "Green scale disc not found — keep the whole sheet (incl. the green scale dot) in frame and flat.";
    result.annotated = canvas.toDataURL("image/jpeg", 0.85);
    return result;
  }
  const refDiaPx = (refDisc.major + refDisc.minor) / 2;
  const scale = TUNABLES.refDiscMm / refDiaPx; // mm per px
  result.scaleMmPx = scale;

  // Beans = dark blobs inside the box. Otsu over in-ROI NON-colored pixels (green would skew it).
  step("measuring beans");
  const roiHist = new Float64Array(256);
  let roiN = 0;
  for (let y = ry0; y <= ry1; y++) for (let x = rx0; x <= rx1; x++) { const i = y * w + x; if (!colored[i]) { roiHist[gray[i]]++; roiN++; } }
  const tDark = otsu(roiHist, roiN);
  const dark = new Uint8Array(N);
  for (let y = ry0; y <= ry1; y++) for (let x = rx0; x <= rx1; x++) { const i = y * w + x; if (gray[i] <= tDark && !colored[i]) dark[i] = 1; }
  const blobs = components(dark, w, h, 30);

  // Beans = neutral-dark blobs inside the bean-zone box. Colored print is already removed, so NO
  // lower size gate — small beans are valid. Only shape sanity + an upper bound remain. Count
  // rejection reasons for debugging.
  const beans: PerBeanResult[] = [];
  const drawn: Blob[] = [];
  let inZone = 0, rejMax = 0, rejAspect = 0, rejExtent = 0;
  for (const b of blobs) {
    if (beanROI && (b.cx < beanROI.x0 || b.cx > beanROI.x1 || b.cy < beanROI.y0 || b.cy > beanROI.y1)) continue;
    inZone++;
    const len = b.major * scale, width = b.minor * scale;
    if (len > TUNABLES.beanMaxLenMm) { rejMax++; continue; }    // upper safety only (no lower gate)
    const aspect = width > 0 ? len / width : 0;
    if (aspect < TUNABLES.beanMinAspect || aspect > TUNABLES.beanMaxAspect) { rejAspect++; continue; }
    if (b.extent < TUNABLES.beanMinExtent) { rejExtent++; continue; }
    beans.push({ len, width, aspect, screen: (width / 25.4) * 64 });
    drawn.push(b);
  }

  // Annotate for visual review, with debug layers so a zero-bean result is diagnosable:
  // cyan = search zone · faint gray = every raw blob · red = scale disc · green = accepted beans.
  const drawEllipse = (b: Blob, color: string, lw: number) => {
    ctx.strokeStyle = color; ctx.lineWidth = lw; ctx.save();
    ctx.translate(b.cx, b.cy); ctx.rotate(b.angle);
    ctx.beginPath(); ctx.ellipse(0, 0, Math.max(b.major, b.minor) / 2, Math.min(b.major, b.minor) / 2, 0, 0, Math.PI * 2);
    ctx.stroke(); ctx.restore();
  };
  const base = Math.max(2, w / 500);
  if (beanROI) { ctx.strokeStyle = "#06b6d4"; ctx.lineWidth = base; ctx.strokeRect(beanROI.x0, beanROI.y0, beanROI.x1 - beanROI.x0, beanROI.y1 - beanROI.y0); }
  for (const b of blobs) drawEllipse(b, "rgba(140,140,140,0.6)", Math.max(1, w / 900));
  drawEllipse(refDisc, "#e11d48", base);
  for (const b of drawn) drawEllipse(b, "#16a34a", base);
  result.annotated = canvas.toDataURL("image/jpeg", 0.85);

  // Cross-check: the box's known long side gives an independent scale. If it matches the disc's,
  // the disc read true and the bean sizes are real; if the box implies a smaller mm/px, the disc
  // over-read (e.g. shadow) and beans are actually smaller. Also surface it as the self-test.
  const boxLongPx = box ? Math.max(box.maxx - box.minx, box.maxy - box.miny) : 0;
  const boxScale = boxLongPx ? TUNABLES.boxLenMm / boxLongPx : 0;
  // Disc-vs-box scale agreement: the box measured with the disc's scale (≈ boxLenMm when they
  // agree). Surfaced in the UI as an always-on scale sanity check.
  const boxMm = boxLongPx ? boxLongPx * scale : null;
  result.selftestCircleMm = boxMm;
  if (boxMm && Math.abs(boxMm - TUNABLES.boxLenMm) / TUNABLES.boxLenMm > 0.03)
    warnings.push(`Scale check: box measured ${boxMm.toFixed(0)} mm vs ${TUNABLES.boxLenMm} — disc & box disagree by >3%, scale suspect.`);
  warnings.push(`dbg: ${blobs.length} blobs · disc ${refDiaPx.toFixed(0)}px→${scale.toFixed(3)} · box ${boxLongPx}px→${boxScale.toFixed(3)} mm/px · inZone ${inZone} · rej ${rejMax}/${rejAspect}/${rejExtent}`);

  if (beans.length === 0) {
    result.error = "No beans passed the filters — check the overlay + the dbg line. (cyan = zone, gray = all blobs, red = scale disc.)";
    return result;
  }

  const widths = beans.map((b) => b.width);
  result.beans = beans;
  result.aggregate = {
    n: beans.length,
    medianLen: median(beans.map((b) => b.len)),
    medianWidth: median(widths),
    medianAspect: median(beans.map((b) => b.aspect)),
    medianScreen: median(beans.map((b) => b.screen)),
    spreadWidth: widths.length > 1 ? quantile(widths, 0.75) - quantile(widths, 0.25) : 0,
  };
  if (beans.length < 8) warnings.push(`Only ${beans.length} beans — a small sample; treat as provisional (aim for 10–20).`);
  step("done");
  result.ok = true;
  return result;
}
